import React, { useMemo } from 'react';
import { useParams } from 'react-router';
import {
  useNavigateWithBreadcrumbs,
  useSyncCurrentOrganization,
} from '../../hooks';
import { ROUTES } from '../../constants';
import i18n from '../../utilities/i18n';
import SitePageHeader from './siteComponents/SiteHeader';
import {
  Button,
  Highlight,
  IconBannersWarning,
  LoadingSpinner,
  VStack,
} from '@adc/parallax-component-library';
import { useQuery } from '@tanstack/react-query';
import {
  devicesBySiteQueryOptions,
  organizationQueryOptions,
  siteQueryOptions,
  studyQueryOptions,
} from '../../queryOptions';
import { useErrorMessageOnEntityView } from '../../hooks/useErrorMessageOnEntityView';
import { QrCodePopup } from '../qrCode/QrCodePopup';
import { siteCapabilitiesQueryOptions } from '../../queryOptions/capability';
import { SubjectsTable } from './siteComponents/SubjectsTable';
import { DevicesTable } from '../devices/DevicesTable';
import { AssignDeviceModal } from '../deviceAssignment/AssignDeviceModal';
import { EntityType } from 'adc-sirius-module-common';

/**
 * Site component that displays the details of a specific site.
 * @returns {React.JSX.Element}
 */
export default function Site() {
  const navigateWithBreadcrumbs = useNavigateWithBreadcrumbs();

  const navigateToSubject = (subjectKey: string, subjectLabel: string) => {
    navigateWithBreadcrumbs(
      ROUTES.SUBJECTS.DETAIL.replace(':subjectKey', subjectKey),
      subjectLabel,
      site.data?.name || i18n.t('Page_Site.title'),
    );
  };

  const navigateToDevice = (deviceKey: string, appName: string) => {
    navigateWithBreadcrumbs(
      ROUTES.DEVICES.DETAIL.replace(':deviceKey', deviceKey),
      appName,
      site.data?.name || i18n.t('Page_Site.title'),
    );
  };

  const { siteKey } = useParams();

  const site = useQuery(siteQueryOptions({ key: siteKey }));

  const { data: capabilities } = useQuery(
    siteCapabilitiesQueryOptions({ key: siteKey }),
  );

  const errorMessage = useErrorMessageOnEntityView(
    site.error,
    'Page_Site.load_error',
  );

  const study = useQuery(studyQueryOptions({ key: site.data?.studyKey }));
  const organization = useQuery(
    organizationQueryOptions({ key: study.data?.organizationKey }),
  );

  const devices = useQuery(
    devicesBySiteQueryOptions(
      {
        key: siteKey,
      },
      { enabled: !!siteKey && !!capabilities?.canGetDevices },
    ),
  );

  useSyncCurrentOrganization(study.data?.organizationKey || '');

  const qrCodeError = useMemo(() => {
    if (!organization.isPending && !organization.data) {
      return true;
    }

    if (!study.isPending && !study.data) {
      return true;
    }

    if (!site.isPending && !site.data) {
      return true;
    }

    return false;
  }, [
    study.isPending,
    study.data,
    organization.isPending,
    organization.data,
    site.isPending,
    site.data,
  ]);

  return (
    <VStack gap={'$4'} height={'100%'}>
      {site.isError ? (
        <Highlight
          variant="danger"
          iconStart={
            <IconBannersWarning
              color="$neutral.0"
              accentColor="$support.danger.default"
            />
          }
          title={i18n.t('Page_Site.load_error_title')}
          body={i18n.t(errorMessage)}
          testID="site-error-banner"
        />
      ) : null}

      {site.isPending ? (
        <VStack alignItems="center" justifyContent="center" flexGrow={1}>
          <LoadingSpinner testID="site-loading" />
        </VStack>
      ) : null}

      {site.isSuccess ? (
        <VStack gap="$4">
          <SitePageHeader
            site={site.data}
            isReadonly={!capabilities?.canUpdateSite}
          >
            <QrCodePopup
              organizationIdentifier={organization.data?.name || ''}
              organizationKey={organization.data?.key || ''}
              studyIdentifier={study.data?.name || ''}
              siteIdentifier={site.data?.name}
              studyKey={study.data?.key || ''}
              siteKey={site.data?.key}
              error={qrCodeError}
              loading={
                study.isPending || organization.isPending || site.isPending
              }
            />
          </SitePageHeader>

          {capabilities?.canGetSubjects ? (
            <SubjectsTable
              siteKey={site.data.key}
              sites={[site.data]}
              navigateToSubject={navigateToSubject}
              canCreateSubject={capabilities?.canInsertSubject}
            />
          ) : null}

          {capabilities?.canGetDevices ? (
            <DevicesTable
              title={i18n.t('Page_Site.table_devices_title')}
              description={i18n.t('Page_Site.table_devices_description')}
              isLoading={devices.isPending}
              hasLoadingError={devices.isError}
              devices={devices.data || []}
              navigateToDevice={navigateToDevice}
              toolbarRightContent={
                capabilities.canManageDeviceAssignments ? (
                  <AssignDeviceModal
                    entityType={EntityType.StudySite}
                    entityKey={site.data.key}
                    entityName={site.data.name}
                    organizationKey={study.data?.organizationKey || ''}
                    trigger={
                      <Button
                        size="medium"
                        label={i18n.t('Page_Site.manage_assignments')}
                        onPress={() => {}}
                        disabled={
                          devices.isPending ||
                          study.isPending ||
                          !study.data?.organizationKey
                        }
                        testID="device-assignments-button"
                      />
                    }
                  />
                ) : undefined
              }
            />
          ) : null}
        </VStack>
      ) : null}
    </VStack>
  );
}
